import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  QaiTableComponent,
  type HeaderConfig,
  TableRow,
  QaiLoadingComponent,
  QaiInnerWrapComponent,
  QaiFieldComponent,
  QaiInputComponent,
  QaiIconComponent,
  QaiSlotDirective
} from '@qai/ui';
import { IIndividualUser } from 'src/app/interfaces/reportingV2/iindividual-user';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { getUsersTabTableHeaders } from 'src/app/utils/configs/reporting/table-configs';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { Subscription } from 'rxjs';
import { LocaleService } from 'src/app/services/locale.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AppConstants } from 'src/app/app-constants';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { CsvExportService, csvValue } from 'src/app/services/general/csv-export.service';
import { localeLowerCase } from '../../../../utils/locale-case.util';

@Component({
  selector: 'app-users-tab',
  standalone: true,
  templateUrl: './users-tab.component.html',
  styleUrls: ['./users-tab.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    }
  ],
  imports: [
    QaiTableComponent,
    QaiLoadingComponent,
    CommonModule,
    QaiInnerWrapComponent,
    QaiFieldComponent,
    QaiInputComponent,
    TranslocoModule,
    FormsModule,
    QaiIconComponent,
    QaiSlotDirective
  ]
})

export class UsersTabComponent implements OnInit, OnChanges, OnDestroy {
  tableData: TableRow[] = [];
  filteredData: TableRow[] = [];
  headers: HeaderConfig[] = [];
  searchTerm = '';
  isLoading = false;
  isTooltipLoading = false;
  isTranslationLoaded = false;
  currentPage = 1;
  private userSub?: Subscription;
  private originalUsers: IIndividualUser[] = [];
  showUserDetail: boolean = false;
  @ViewChild(QaiTableComponent) tableRef!: QaiTableComponent;
  @Input() selectedAccount: { label: string; value: string } | null = null;
  @Input() selectedTeams: string[] = [];
  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private router: Router,
    private amplitudeService: AmplitudeService,
    private reportingDashboardService: ReportingDashboardService,
    private localeService: LocaleService,
    private featureFlagsService: FeatureFlagsService,
    private csvExportService: CsvExportService,
  ) {}

  async ngOnInit() {
    this.translationService.loadScopedTranslation('reportingV2').then(async () => {
      this.isTranslationLoaded = true;
      this.showUserDetail = await this.featureFlagsService.isFlagEnabled(
        AppConstants.INDIVIDUAL_USER_VIEW
      );
      console.log(
        `Flag for INDIVIDUAL_ASSIGNMENT_VIEW: ${this.showUserDetail}`
      );
      this.initializeTable();
      this.loadUsers();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['selectedAccount'] && !changes['selectedAccount'].firstChange) ||
      (changes['selectedTeams'] && !changes['selectedTeams'].firstChange)) {
      this.loadUsers();
    }
  }

  private initializeTable(): void {
    this.headers = getUsersTabTableHeaders(this.translocoService);
  }

  pageChanged(event: { page: number }): void {
    this.currentPage = event.page;
  }

  onSortChanged(event: any): void {
    console.log('Sort changed:', event);
  }

  public loadUsers(): void {
    // Cancel any ongoing request before starting a new one
    if (this.userSub) {
      this.userSub.unsubscribe();
    }
    if (!this.selectedAccount?.value) {
      console.log('No account selected, skipping users-tab load');
      return;
    }
    this.isLoading = true;
    const accountUuid = this.selectedAccount.value;
    const teamsToSend = this.selectedTeams.includes('all') ? [] : this.selectedTeams;
    this.userSub = this.reportingDashboardService.getUsersData(accountUuid, teamsToSend, withDemoModule(AppConstants.NEW_REPORTING_UI)).subscribe({
      next: (users: IIndividualUser[]) => {
        this.originalUsers = users;
        this.tableData = this.transformApiDataToTableRows(users);
        this.filteredData = this.tableData;
        this.currentPage = 1;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Failed to load users data:', error);
        this.isLoading = false;
      }
    });
  }

  private transformApiDataToTableRows(apiData: IIndividualUser[]): TableRow[] {
    return apiData.map(user => this.createUserTableRow(user));
  }

  private createUserTableRow(user: IIndividualUser): TableRow {
    const safeValue = (val: any, formatter?: (v: any) => string): string => {
      if (val === null || val === undefined) {
        return '—';
      }
      return formatter ? formatter(val) : val.toString();
    };

    const row = {
      id: user.userUuid,
      [this.headers[0].name]: { type: 'text', value: safeValue(user.name) },
      [this.headers[1].name]: { type: 'text', value: safeValue(user.email) },
      [this.headers[2].name]: this.getTeamCellData(user.memberOfTeams),
      [this.headers[3].name]: {
        type: 'text',
        value: safeValue(user.numberOfAssignments, v => this.localeService.formatNumber(v))
      },
      [this.headers[4].name]: {
        type: 'text',
        value: safeValue(user.totalSessions, v => this.localeService.formatNumber(v))
      },
      [this.headers[5].name]: {
        type: 'text',
        value: safeValue(user.averageScore, v => this.localeService.formatNumber(v,1))
      },
      [this.headers[6].name]: {
        type: 'text',
        value: safeValue(user.lastPracticeDate, v => this.localeService.formatTableDate(v)),
        ...csvValue(this.localeService.formatCsvDate(user.lastPracticeDate))
      },
     [this.headers[7].name]: {
        type: 'text',
        value:
          user.certifiedAssignments == null || user.totalCertificationAssignments == null
            ? '—'
            : `${this.localeService.formatNumber(user.certifiedAssignments)} ${this.translocoService.translate('OF_LABEL', {}, 'reportingV2')} ${this.localeService.formatNumber(user.totalCertificationAssignments)}`
      },
      [this.headers[8].name]: {
        type: 'text',
        value:
          user.proficientAssignments == null || user.totalPracticeAssignments == null
            ? '—'
            : `${this.localeService.formatNumber(user.proficientAssignments)} ${this.translocoService.translate('OF_LABEL', {}, 'reportingV2')} ${this.localeService.formatNumber(user.totalPracticeAssignments)}`
      }
    };
    return row as unknown as TableRow;
  }

  private getTeamCellData(memberOfTeams: string) {
    const teamsArray = memberOfTeams
      ? memberOfTeams.split('||').map(t => t.trim())
      : [];
    if (teamsArray.length > 1) {
      return {
        type: 'tooltip',
        value: teamsArray,
        label: `${teamsArray.length} teams`,
      };
    }
    return {
      type: 'text',
      value: teamsArray[0] || '-',
    };
  }

  onTooltipDataRequested = ({ row }: { row: TableRow }) => {
    this.isTooltipLoading = true;
    const cell = row[this.headers[2].name];
    if (cell.type !== 'tooltip') {
      this.isTooltipLoading = false;
      return;
    }
    setTimeout(() => {
      this.tableRef?.setTooltipData(cell.value);
      this.isTooltipLoading = false;
    }, 300);
  };

  onTableRowClick(event: { rowId: string | number; rowData: TableRow }) {
    console.log('Row clicked:', event);
    if (this.showUserDetail) {
      this.navigateToUserDetail(event.rowId);
    }
  }

  navigateToUserDetail(userId: string | number) {
    this.router.navigate(['/reporting_dashboard/user', userId]);
  }

  private getColumnName(translationKey: string): string {
    return this.translocoService.translate(translationKey, {}, 'reportingV2');
  }

  private extractTeamsData(cell: any): string {
    if (!cell) return '';
    if (cell.type === 'text') return cell.value || '';
    if (cell.type === 'tooltip' && Array.isArray(cell.value)) return cell.value.join(', ');
    return '';
  }

  private extractCellValue(cell: any): string {
    return cell?.type === 'text' && cell.value ? cell.value : '';
  }

  protected onSearchChange(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.searchTerm = value;
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_ASSIGNMENTSEARCH);
    this.onSearch();
  }

  onSearch(): void {
    const activeLang = this.translocoService.getActiveLang() || 'en';
    // REMEDIATE: user-typed search input
    const normalizedTerm = localeLowerCase(this.searchTerm, activeLang).trim();
    this.filteredData = !normalizedTerm
      ? this.tableData
      : this.tableData.filter(row => {
        // REMEDIATE: user display name matched against search
        const nameCell = localeLowerCase(this.extractCellValue(row[this.headers[0].name]), activeLang);
        // REMEDIATE: email matched against search
        const emailCell = localeLowerCase(this.extractCellValue(row[this.headers[1].name]), activeLang);
        const teamsCell = row[this.headers[2].name];
        let teams = '';

        if (teamsCell?.type === 'tooltip') {
          // REMEDIATE: team names matched against search
          teams = localeLowerCase((teamsCell.value as string[]).join(' '), activeLang);
        } else if (teamsCell?.type === 'text') {
          // REMEDIATE: team name matched against search
          teams = localeLowerCase(String(teamsCell.value), activeLang);
        }

        return (
          nameCell.includes(normalizedTerm) ||
          emailCell.includes(normalizedTerm) ||
          teams.includes(normalizedTerm)
        );
      });

    this.currentPage = 1;
  }

  protected onDownloadClick(): void {
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTING_USERS_CSV_EXPORTED);

    const accountName = this.selectedAccount?.label ?? 'account';
    const fileName = this.csvExportService.getCsvFileName(accountName, 'users');

    const usersMap = new Map<string, IIndividualUser>(
      this.originalUsers.map(u => [u.userUuid, u])
    );

    const teamsColumnIndex = 2;
    const assignmentsColumnIndex = 3;
    const assignmentsColumnHeader = 'Assignments';

    const headerNames = this.headers.map((h, i) =>
      i === assignmentsColumnIndex ? assignmentsColumnHeader : h.name
    );

    const csvData = this.filteredData.map(row => {
      const originalUser = usersMap.get(row['id'] as string);

      const teamsArray = originalUser?.memberOfTeams
        ? originalUser.memberOfTeams.split('||').map(t => t.trim()).filter(Boolean)
        : [];

      const assignmentNamesArray = originalUser?.assignmentNames
        ? originalUser.assignmentNames.split(',').map(n => n.trim()).filter(Boolean)
        : [];

      const csvRow: Record<string, unknown> = {};

      this.headers.forEach((header, index) => {
        const colName = index === assignmentsColumnIndex ? assignmentsColumnHeader : header.name;
        if (index === teamsColumnIndex) {
          csvRow[colName] = `[${teamsArray.map(t => JSON.stringify(t)).join(', ')}]`;
        } else if (index === assignmentsColumnIndex) {
          csvRow[colName] = `[${assignmentNamesArray.map(n => JSON.stringify(n)).join(', ')}]`;
        } else {
          const cell = row[header.name] as { value?: unknown; csvValue?: unknown };
          csvRow[colName] = cell?.csvValue ?? cell?.value ?? '';
        }
      });

      return csvRow;
    });

    this.csvExportService.exportToCsv(csvData, fileName, headerNames);
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
  }
}